import { useQuery } from '@tanstack/react-query';
import {
  type PropsWithChildren,
  createContext,
  useMemo,
  useContext,
} from 'react';
import { useParams } from 'react-router-dom';
import { mcpServerQuery } from 'query/mcp';
import { KizenError } from 'services/errors';
import { getOriginalErrorStatusCode } from 'services/AxiosService';
import useCan from 'hooks/useCan';
import { MCP_SERVER_SETTINGS_PERMISSION_KEY } from 'pages/MCPServerSettings/constants';
import {
  getVisibleMCPServerDetailRoutePatterns,
  getVisibleMCPServerDetailSubroutes,
} from './navigation';

type MCPServerDetailPageValue = ReturnType<typeof useMCPServerDetail>;

const MCPServerDetailPageContext = createContext<
  MCPServerDetailPageValue | undefined
>(undefined);

const useMCPServerDetail = () => {
  const { mcpServerId } = useParams<{ mcpServerId: string }>();

  const queryOptions = useMemo(
    () =>
      mcpServerQuery(mcpServerId, {
        staleTime: 0,
        enabled: !!mcpServerId,
      }),
    [mcpServerId]
  );

  const query = useQuery(queryOptions);
  const server = query.data;

  const canWrite = useCan({
    edit: 'sections',
    for: MCP_SERVER_SETTINGS_PERMISSION_KEY,
  });

  const canDeleteSection = useCan({
    remove: 'sections',
    for: MCP_SERVER_SETTINGS_PERMISSION_KEY,
  });

  const isSystemManaged = server?.is_system_managed ?? false;
  const canDelete = canDeleteSection && !isSystemManaged;

  const subroutes = useMemo(
    () =>
      mcpServerId
        ? getVisibleMCPServerDetailSubroutes(mcpServerId, canWrite)
        : [],
    [mcpServerId, canWrite]
  );

  const routePatterns = useMemo(
    () => getVisibleMCPServerDetailRoutePatterns(canWrite),
    [canWrite]
  );

  return {
    server,
    query,
    subroutes,
    routePatterns,
    canWrite,
    canDelete,
    isSystemManaged,
  };
};

export const useMCPServerDetailPage = () => {
  const ctx = useContext(MCPServerDetailPageContext);

  if (!ctx) {
    throw Error(
      'useMCPServerDetailPage must be used within MCPServerDetailProvider'
    );
  }

  return ctx;
};

export const MCPServerDetailProvider = ({ children }: PropsWithChildren) => {
  const ctx = useMCPServerDetail();
  const { error, isError } = ctx.query;

  if (isError) {
    throw new KizenError(getOriginalErrorStatusCode(error) ?? 404);
  }

  return (
    <MCPServerDetailPageContext.Provider value={ctx}>
      {children}
    </MCPServerDetailPageContext.Provider>
  );
};
